import * as React from "react";
import { Button } from "@/components/ui/button";
import { Plus, Variable, RefreshCw, Replace } from "lucide-react";
import { ProjectVariable } from "../../../../types/navigationTypes";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { PanelSectionHeader, PanelSearchField } from "@/taskpane/shared/components/panel-layout";

interface ProjectVariablesHeaderProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  variables: ProjectVariable[];
  onCreateVariable: () => void;
  onScanDocument: () => void;
  isScanning?: boolean;
  onReplaceAll: () => void;
  isReplacing?: boolean;
  variablesWithValue: number;
}

const ProjectVariablesHeader: React.FC<ProjectVariablesHeaderProps> = ({
  searchTerm,
  onSearchChange,
  variables,
  onCreateVariable,
  onScanDocument,
  isScanning = false,
  onReplaceAll,
  isReplacing = false,
  variablesWithValue,
}) => {
  const trailing = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        onClick={onScanDocument}
        size="sm"
        className="h-8 text-xs px-3"
        disabled={isScanning}
        variant="outline"
        title="Scan document"
      >
        <RefreshCw size={ICON_SIZES.sm} className={isScanning ? "animate-spin" : ""} />
      </Button>
      <Button
        onClick={onReplaceAll}
        size="sm"
        className="h-8 text-xs px-3"
        disabled={isReplacing || variablesWithValue === 0}
        variant="outline"
      >
        <Replace size={ICON_SIZES.sm} className={isReplacing ? "animate-spin mr-1.5" : "mr-1.5"} />
        {isReplacing ? "Replacing…" : `Replace all${variablesWithValue > 0 ? ` (${variablesWithValue})` : ""}`}
      </Button>
      <Button onClick={onCreateVariable} size="sm" className="h-8 text-xs px-3">
        <Plus size={ICON_SIZES.sm} className="mr-1.5" />
        New variable
      </Button>
    </div>
  );

  return (
    <div className="mb-1">
      <PanelSectionHeader
        icon={Variable}
        title="Project variables"
        count={variables.length > 0 ? variables.length : undefined}
        countLabel="variables"
        trailing={trailing}
        className="mb-4"
      />

      {variables.length > 0 ? (
        <div className="mb-4 flex items-center gap-2">
          <PanelSearchField
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Search variables…"
            className="w-full"
          />
        </div>
      ) : null}
    </div>
  );
};

export default ProjectVariablesHeader;
